/**
 * BootHealthGate — Mar 2026
 *
 * Wraps the entire SPA. On mount + every 60s it pings
 * `GET /api/site-state` and decides one of three things:
 *
 *   1. Reachable + maintenance_mode=false  → render the app
 *      (passes children through).
 *   2. Reachable + maintenance_mode=true   → show fallback page UNLESS
 *      the user is on the admin portal (`/config/*`). Boutique team
 *      must keep admin access during maintenance windows.
 *   3. Unreachable (network / 5xx / timeout) → show fallback page in
 *      "down" mode after a short failure streak. Same admin-bypass
 *      rule applies, so admin can still diagnose if reverse-proxy +
 *      auth are healthy but the app DB is down.
 *
 * May 26 2026 — failure-tolerant gate. Long-running customer flows
 * (esp. Step 3 AI preview back-view generation) cause one-off probe
 * timeouts when the backend is busy with the Gemini upstream + heavy
 * Mongo writes. Previously a SINGLE blip flipped the whole app to
 * "Our needle slipped" — the customer's preview was generating fine in
 * the background, but the SPA had already evicted their state.
 * Fix: require 3 consecutive failures (≈ 3 × 60s = 3 minutes) before
 * declaring the app down. Each successful probe resets the streak.
 *
 * The component is intentionally lightweight — no axios interceptor,
 * no React Query — to keep the first-paint critical path tiny.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

import ServiceUnavailable from "./ServiceUnavailable";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;
const REQ_TIMEOUT_MS = 12_000;
const FAILURE_STREAK_LIMIT = 3;  // need 3 consecutive failures to flip to "down"


export default function BootHealthGate({ children }) {
    const [phase, setPhase] = useState("checking");   // checking | ok | maintenance | down
    const [maintMsg, setMaintMsg] = useState("");
    const location = useLocation();
    const aliveRef = useRef(true);
    const failStreakRef = useRef(0);

    const probe = useCallback(async () => {
        // AbortController gives us a strict timeout — if the server is
        // hung (no TCP close, no body) we shouldn't keep the SPA
        // blocked on the spinner forever.
        const ctrl = new AbortController();
        const to = setTimeout(() => ctrl.abort(), REQ_TIMEOUT_MS);
        try {
            const r = await fetch(`${API}/site-state`, { signal: ctrl.signal, cache: "no-store" });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            if (!aliveRef.current) return;
            failStreakRef.current = 0;
            if (data.maintenance_mode) {
                setMaintMsg(data.maintenance_message || "");
                setPhase("maintenance");
            } else {
                setPhase("ok");
            }
        } catch (e) {
            if (!aliveRef.current) return;
            failStreakRef.current += 1;
            // First failure on initial boot still needs a verdict (otherwise
            // we'd sit on the spinner forever). After the first probe we
            // need the full streak to flip a healthy SPA to "down".
            if (phase === "checking" || failStreakRef.current >= FAILURE_STREAK_LIMIT) {
                setPhase("down");
            }
        } finally {
            clearTimeout(to);
        }
    }, [phase]);

    useEffect(() => {
        aliveRef.current = true;
        probe();
        const id = setInterval(probe, POLL_MS);
        return () => { aliveRef.current = false; clearInterval(id); };
    }, [probe]);

    // Admin needs uninterrupted access — bypass the gate for /config/*.
    const isAdminRoute = location.pathname.startsWith("/config");

    if (phase === "checking") {
        // First-paint: show a tiny on-brand spinner so the user doesn't
        // see a blank white frame. Keeps it under 200ms in practice.
        return (
            <div className="min-h-screen w-full bg-ivory flex items-center justify-center" data-testid="boot-checking">
                <div className="w-8 h-8 rounded-full border-2 border-rosegold border-t-transparent animate-spin" />
            </div>
        );
    }

    if (phase === "maintenance" && !isAdminRoute) {
        return <ServiceUnavailable mode="maintenance" customMessage={maintMsg} onRetry={probe} />;
    }

    if (phase === "down" && !isAdminRoute) {
        return <ServiceUnavailable mode="down" onRetry={probe} />;
    }

    return children;
}
